import { BIOME, BLOCK, TREE, type Biome, type BlockKind, type TreeType } from "../types";

export const SURFACE = {
    Grass: 0,
    SnowyGrass: 1,
    CoarseDirt: 2,
    Sand: 3,
    Terracotta: 4,
    Mycelium: 5,
} as const;

export type SurfaceKind = (typeof SURFACE)[keyof typeof SURFACE];
export type RiverFluid = typeof BLOCK.Water | typeof BLOCK.Ice;

export interface DecorationRule {
    type: BlockKind;
    density: number;
    placeOn: readonly BlockKind[];
    placeAbove?: typeof BLOCK.Water;
    height?: number;
}

export interface BiomeRule {
    allowRivers: boolean;
    riverFluid: RiverFluid | null;
    snowLayerAlways: boolean;
    surface: SurfaceKind;
    trees?: {
        types: readonly TreeType[];
        density: number;
        minDistance: number;
    };
    decorations?: readonly DecorationRule[];
}

const MESA_SURFACES = [
    BLOCK.Terracotta,
    BLOCK.RedTerracotta,
    BLOCK.WhiteTerracotta,
] as const;

export const BIOME_RULES: Record<Biome, BiomeRule> = {
    [BIOME.Icy]: {
        allowRivers: true,
        riverFluid: BLOCK.Ice,
        snowLayerAlways: true,
        surface: SURFACE.SnowyGrass,
        decorations: [
            { type: BLOCK.IceSpike, density: 0.04, placeOn: [BLOCK.SnowBlock, BLOCK.Ice], height: 2 },
        ],
    },
    [BIOME.ColdForest]: {
        allowRivers: true,
        riverFluid: BLOCK.Water,
        snowLayerAlways: true,
        surface: SURFACE.Grass,
        trees: { types: [TREE.Pine], density: 0.18, minDistance: 5 },
        decorations: [
            { type: BLOCK.Rock, density: 0.03, placeOn: [BLOCK.Grass] },
        ],
    },
    [BIOME.Taiga]: {
        allowRivers: true,
        riverFluid: BLOCK.Water,
        snowLayerAlways: false,
        surface: SURFACE.Grass,
        trees: { types: [TREE.Pine], density: 0.22, minDistance: 5 },
        decorations: [
            { type: BLOCK.Rock, density: 0.025, placeOn: [BLOCK.Grass] },
            { type: BLOCK.Mushroom, density: 0.02, placeOn: [BLOCK.Grass, BLOCK.Dirt] },
        ],
    },
    [BIOME.Temperate]: {
        allowRivers: true,
        riverFluid: BLOCK.Water,
        snowLayerAlways: false,
        surface: SURFACE.Grass,
        trees: { types: [TREE.Oak], density: 0.18, minDistance: 7 },
        decorations: [
            { type: BLOCK.FlowerRed, density: 0.05, placeOn: [BLOCK.Grass] },
            { type: BLOCK.FlowerYellow, density: 0.05, placeOn: [BLOCK.Grass] },
            { type: BLOCK.Rock, density: 0.02, placeOn: [BLOCK.Grass] },
        ],
    },
    [BIOME.Humid]: {
        allowRivers: true,
        riverFluid: BLOCK.Water,
        snowLayerAlways: false,
        surface: SURFACE.Grass,
        trees: { types: [TREE.Oak, TREE.Pine], density: 0.22, minDistance: 7 },
        decorations: [
            { type: BLOCK.Mushroom, density: 0.04, placeOn: [BLOCK.Grass, BLOCK.Dirt] },
            { type: BLOCK.LilyPad, density: 0.25, placeOn: [BLOCK.Water], placeAbove: BLOCK.Water },
        ],
    },
    [BIOME.Swamp]: {
        allowRivers: true,
        riverFluid: BLOCK.Water,
        snowLayerAlways: false,
        surface: SURFACE.CoarseDirt,
        trees: { types: [TREE.Oak], density: 0.14, minDistance: 7 },
        decorations: [
            { type: BLOCK.Mushroom, density: 0.05, placeOn: [BLOCK.CoarseDirt, BLOCK.Dirt, BLOCK.Grass] },
            { type: BLOCK.TallGrass, density: 0.06, placeOn: [BLOCK.CoarseDirt, BLOCK.Grass] },
            { type: BLOCK.LilyPad, density: 0.4, placeOn: [BLOCK.Water], placeAbove: BLOCK.Water },
        ],
    },
    [BIOME.Savanna]: {
        allowRivers: false,
        riverFluid: BLOCK.Water,
        snowLayerAlways: false,
        surface: SURFACE.Grass,
        trees: { types: [TREE.Oak], density: 0.09, minDistance: 9 },
        decorations: [
            { type: BLOCK.TallGrass, density: 0.08, placeOn: [BLOCK.Grass] },
            { type: BLOCK.DeadBush, density: 0.025, placeOn: [BLOCK.Grass, BLOCK.Sand] },
            { type: BLOCK.Rock, density: 0.02, placeOn: [BLOCK.Grass] },
        ],
    },
    [BIOME.Desert]: {
        allowRivers: false,
        riverFluid: null,
        snowLayerAlways: false,
        surface: SURFACE.Sand,
        decorations: [
            { type: BLOCK.Cactus, density: 0.03, placeOn: [BLOCK.Sand], height: 2 },
            { type: BLOCK.DeadBush, density: 0.03, placeOn: [BLOCK.Sand] },
            { type: BLOCK.Rock, density: 0.02, placeOn: [BLOCK.Sand] },
        ],
    },
    [BIOME.Mesa]: {
        allowRivers: false,
        riverFluid: null,
        snowLayerAlways: false,
        surface: SURFACE.Terracotta,
        decorations: [
            { type: BLOCK.DeadBush, density: 0.03, placeOn: MESA_SURFACES },
            { type: BLOCK.Rock, density: 0.02, placeOn: MESA_SURFACES },
        ],
    },
    [BIOME.Mushroom]: {
        allowRivers: true,
        riverFluid: BLOCK.Water,
        snowLayerAlways: false,
        surface: SURFACE.Mycelium,
        trees: { types: [TREE.MushroomRed, TREE.MushroomBrown], density: 0.2, minDistance: 7 },
        decorations: [
            { type: BLOCK.Mushroom, density: 0.06, placeOn: [BLOCK.Mycelium] },
        ],
    },
};

const MESA_STRATA: BlockKind[] = [
    BLOCK.Terracotta,
    BLOCK.Terracotta,
    BLOCK.RedTerracotta,
    BLOCK.Terracotta,
    BLOCK.WhiteTerracotta,
    BLOCK.Terracotta,
    BLOCK.Terracotta,
    BLOCK.RedTerracotta,
];

export function mesaStrataAt(y: number, seed: number): BlockKind {
    const shift = Math.abs(seed) % MESA_STRATA.length;
    return MESA_STRATA[(y + shift) % MESA_STRATA.length];
}

/**
 * Kept for narrative completeness; per-cell biomes derive directly from noise
 * now, so this map is no longer queried at generation time.
 */
export const BIOME_COMPATIBILITY: Record<Biome, Biome[]> = {
    [BIOME.Icy]: [BIOME.ColdForest],
    [BIOME.ColdForest]: [BIOME.Icy, BIOME.Taiga],
    [BIOME.Taiga]: [BIOME.ColdForest, BIOME.Humid],
    [BIOME.Temperate]: [BIOME.Humid, BIOME.Savanna, BIOME.Swamp],
    [BIOME.Humid]: [BIOME.Temperate, BIOME.Swamp, BIOME.Taiga],
    [BIOME.Swamp]: [BIOME.Humid, BIOME.Temperate],
    [BIOME.Savanna]: [BIOME.Desert, BIOME.Temperate],
    [BIOME.Desert]: [BIOME.Savanna, BIOME.Mesa],
    [BIOME.Mesa]: [BIOME.Desert, BIOME.Savanna],
    [BIOME.Mushroom]: [],
};
